import React, { useState, useEffect, useMemo, useRef } from 'react';
import ConfirmDialog from './ConfirmDialog';
import ShopMatchCard from './ShopMatchCard';
import FilterChip from './FilterChip';
import MapLegend from './MapLegend';
import ShopSearchRecoveryPanel from './ShopSearchRecoveryPanel';
import { buildShopSearchRecovery } from './shopSearchRecovery';
import { buildShopMapPopupHtml, createShopMapMarker } from './shopMapPopupCard';
import { createShopClusterer } from './shopMapClusters';
import { buildShopPopupViewModel, getShopGoogleMapsUrl, shopOffersService } from './shopDisplayHelpers';
import { SHOP_SERVICE_OPTIONS } from '../utils/shopServices';
import RequestAttachments from './RequestAttachments';
import UseMyLocationButton from './UseMyLocationButton';
import { mapStyleUrl } from '../utils/theme';

import { API_BASE } from '../utils/apiBase';
// The request coach is optional: if it does not respond within this window we close the
// reviewing popup and let the user proceed, so a slow or unavailable AI never blocks sending.
const REVIEW_TIMEOUT_MS = 10000;
const OTHER = '__OTHER__';
const DEFAULT_CENTER = [-98.5795, 39.8283];
const MAPBOX_JS_URL = 'https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.js';
const MAPBOX_CSS_URL = 'https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.css';
const RADIUS_SOURCE_ID = 'vehicle-selector-radius-source';
const RADIUS_FILL_LAYER_ID = 'vehicle-selector-radius-fill';
const RADIUS_OUTLINE_LAYER_ID = 'vehicle-selector-radius-outline';
const MOBILE_RADIUS_SOURCE_ID = 'vehicle-selector-mobile-radius-source';
const MOBILE_RADIUS_FILL_LAYER_ID = 'vehicle-selector-mobile-radius-fill';
const MOBILE_RADIUS_OUTLINE_LAYER_ID = 'vehicle-selector-mobile-radius-outline';
const MOBILE_RADIUS_COLOR = '#7c3aed';

const RADIUS_OPTIONS = ['5', '10', '25', '50', '100', '250'];

// Shops in the selection list are paged rather than crammed into a short scroll box.
const SHOPS_PER_PAGE = 5;
const OWNER_ONBOARDING_DISMISS_KEY = 'gearcheckauto:owner-first-request-onboarding-dismissed';

function loadMapboxAssets() {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);

  return new Promise((resolve, reject) => {
    const existingCss = document.querySelector(`link[href="${MAPBOX_CSS_URL}"]`);
    if (!existingCss) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = MAPBOX_CSS_URL;
      document.head.appendChild(css);
    }

    const existingScript = document.querySelector(`script[src="${MAPBOX_JS_URL}"]`);
    if (existingScript) {
      if (window.mapboxgl) {
        resolve(window.mapboxgl);
        return;
      }
      existingScript.addEventListener('load', () => resolve(window.mapboxgl), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Mapbox script')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = MAPBOX_JS_URL;
    script.async = true;
    script.onload = () => resolve(window.mapboxgl);
    script.onerror = () => reject(new Error('Failed to load Mapbox script'));
    document.body.appendChild(script);
  });
}

function buildCircleGeoJson(center, radiusMiles, points = 64) {
  const [centerLon, centerLat] = center;
  const earthRadiusMiles = 3959;
  const angularDistance = radiusMiles / earthRadiusMiles;
  const centerLatRad = (centerLat * Math.PI) / 180;
  const centerLonRad = (centerLon * Math.PI) / 180;
  const coordinates = [];

  for (let i = 0; i <= points; i += 1) {
    const bearing = (2 * Math.PI * i) / points;
    const latRad = Math.asin(
      Math.sin(centerLatRad) * Math.cos(angularDistance) +
      Math.cos(centerLatRad) * Math.sin(angularDistance) * Math.cos(bearing)
    );
    const lonRad = centerLonRad + Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(centerLatRad),
      Math.cos(angularDistance) - Math.sin(centerLatRad) * Math.sin(latRad)
    );

    coordinates.push([(lonRad * 180) / Math.PI, (latRad * 180) / Math.PI]);
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates],
    },
  };
}

// Draw violet service-area circles for the given mobile mechanics. Called with only the
// currently-unclustered shops on every moveend, so a radius shows when its pin is separated
// out and hides while it's inside a cluster. The source + layers are created once and then
// updated in place with setData, so the circles never flicker/repaint when the map settles
// (a remove/add teardown each moveend was causing a visible lag).
function renderMobileRadiusLayers(map, mobileShops) {
  if (!map || typeof map.getSource !== 'function') return;

  const features = (mobileShops || [])
    .filter(
      (shop) =>
        shop.is_mobile_mechanic &&
        Number.isFinite(shop.latitude) &&
        Number.isFinite(shop.longitude) &&
        Number.isFinite(Number(shop.mobile_service_radius_miles))
    )
    .map((shop) => buildCircleGeoJson([shop.longitude, shop.latitude], Number(shop.mobile_service_radius_miles)));
  const data = { type: 'FeatureCollection', features };

  const existing = map.getSource(MOBILE_RADIUS_SOURCE_ID);
  if (existing && typeof existing.setData === 'function') {
    existing.setData(data);
    return;
  }
  map.addSource(MOBILE_RADIUS_SOURCE_ID, { type: 'geojson', data });
  map.addLayer({
    id: MOBILE_RADIUS_FILL_LAYER_ID,
    type: 'fill',
    source: MOBILE_RADIUS_SOURCE_ID,
    paint: { 'fill-color': MOBILE_RADIUS_COLOR, 'fill-opacity': 0.12 },
  });
  map.addLayer({
    id: MOBILE_RADIUS_OUTLINE_LAYER_ID,
    type: 'line',
    source: MOBILE_RADIUS_SOURCE_ID,
    paint: { 'line-color': MOBILE_RADIUS_COLOR, 'line-width': 1.5, 'line-dasharray': [2, 1] },
  });
}


const VehicleSelector = ({ token, onLogout, onSessionExpired, isShopUser = false, currentUserId = '', userZipCode = '', demoMode = false, onGoToMessages }) => {
  const runtimeToken =
    typeof window !== 'undefined' && window.__ENV__ && typeof window.__ENV__.REACT_APP_MAPBOX_TOKEN === 'string'
      ? window.__ENV__.REACT_APP_MAPBOX_TOKEN
      : '';
  const mapToken = runtimeToken || process.env.REACT_APP_MAPBOX_TOKEN || '';
  const mapContainerRef = useRef(null);
  const requestCoachRef = useRef(null);
  const statusRef = useRef(null);
  const firstVehicleFieldRef = useRef(null);
  const mapRef = useRef(null);
  const mapboxRef = useRef(null);
  const clustererRef = useRef(null);
  const searchCenterMarkerRef = useRef(null);
  const favoriteShopIdsRef = useRef([]);

  const [makes, setMakes] = useState([]);
  const [years, setYears] = useState([]);
  const [models, setModels] = useState([]);
  const [trims, setTrims] = useState([]);
  const [descriptions, setDescriptions] = useState([]);

  const [selectedMake, setSelectedMake] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedTrim, setSelectedTrim] = useState('');
  const [selectedDescription, setSelectedDescription] = useState('');

  // ✅ "Other..." typed values
  const [otherMake, setOtherMake] = useState('');
  const [otherYear, setOtherYear] = useState('');
  const [otherModel, setOtherModel] = useState('');
  const [otherTrim, setOtherTrim] = useState('');
  const [otherDescription, setOtherDescription] = useState('');

  const effectiveMake = selectedMake === OTHER ? otherMake.trim() : selectedMake;
  const effectiveYear = selectedYear === OTHER ? otherYear.trim() : selectedYear;
  const effectiveModel = selectedModel === OTHER ? otherModel.trim() : selectedModel;
  const effectiveTrim = selectedTrim === OTHER ? otherTrim.trim() : selectedTrim;
  const effectiveDescription = selectedDescription === OTHER ? otherDescription.trim() : selectedDescription;

  const [userMessage, setUserMessage] = useState('');
  const [requestAttachmentState, setRequestAttachmentState] = useState({ attachmentIds: [], uploading: false, count: 0 });
  const requestAttachmentsRef = useRef(null);
  const [zipCode, setZipCode] = useState('');
  // A demo visitor has nothing to type and no reason to guess, so start them on a radius wide
  // enough to reach the seeded Twin Cities shops from the 55401 ZIP their account carries.
  const [selectedRadius, setSelectedRadius] = useState(demoMode ? '25' : '5');
  // Set once the user edits the ZIP themselves, so the profile prefill below never overwrites
  // what they typed. Same guard ShopExplorerPage uses for its own prefill.
  const [zipTouched, setZipTouched] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [sendingRequest, setSendingRequest] = useState(false);
  // Single-shop reminder: the site's value is comparing responses from several shops, so
  // sending to exactly one asks for a quick acknowledgment first (every time). The ref
  // lets the confirmed re-entry skip the dialog for that one attempt.
  const [showSingleShopConfirm, setShowSingleShopConfirm] = useState(false);
  const singleShopAckRef = useRef(false);
  // Demo only: asked once per selection when the visitor is about to send without their own shop.
  const [showDemoShopConfirm, setShowDemoShopConfirm] = useState(false);
  const demoShopAckRef = useRef(false);
  const statusTimeoutRef = useRef(null);
  const lastMapFitKeyRef = useRef('');

  // Shop search / selection state
  const [shops, setShops] = useState([]);
  const [searchDiagnostics, setSearchDiagnostics] = useState(null);
  const [loadingShops, setLoadingShops] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [filterSpecialist, setFilterSpecialist] = useState(true);
  // Demo only. The demo prefills the ZIP, so the search runs on arrival before a make is picked -
  // and with specialist-only and no make, is_specialist_for_make is false for every shop, so the
  // visitor would land on "adjust your filters" over a search that actually found plenty.
  // Outside the demo there is no prefill, so the search does not run until the user has chosen a
  // vehicle, and the specialist-only default behaves as it always has. Left alone deliberately:
  // this is the non-demo default and changing it is not part of the demo work.
  const [filterGeneral, setFilterGeneral] = useState(demoMode);
  const [filterFavoritesOnly, setFilterFavoritesOnly] = useState(false);
  const [filterService, setFilterService] = useState("");
  const [mobileFilter, setMobileFilter] = useState("include"); // include | only | hide
  const [selectedShopIds, setSelectedShopIds] = useState([]);
  const [shopPage, setShopPage] = useState(0);

  // Changing which shops are selected starts a fresh decision - ask about a single-shop
  // send, and about the missing demo shop, again on the next attempt.
  useEffect(() => {
    singleShopAckRef.current = false;
    demoShopAckRef.current = false;
  }, [selectedShopIds]);

  // Demo only: seed the ZIP from the account so the shop search runs on arrival instead of showing
  // an empty form - the demo owner carries 55418, so a visitor lands straight on the seeded Twin
  // Cities shops with a 25-mile radius. Skipped once the visitor edits the field themselves.
  //
  // Gated on demoMode on purpose. Prefilling for real users would auto-run the search before they
  // have picked a vehicle, which is the state that needs filterGeneral defaulted on, and that is a
  // non-demo behaviour change this work should not be making.
  useEffect(() => {
    if (!demoMode) return;
    const nextUserZip = String(userZipCode || '').trim();
    if (!zipTouched && nextUserZip) {
      setZipCode(nextUserZip);
    }
  }, [demoMode, userZipCode, zipTouched]);
  const [favoriteShopIds, setFavoriteShopIds] = useState([]);
  const [zipSearchCenter, setZipSearchCenter] = useState(null);
  const [mapError, setMapError] = useState('');
  const [mapReady, setMapReady] = useState(false);
  const [requestReview, setRequestReview] = useState(null);
  const [reviewVisible, setReviewVisible] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewPromptedKey, setReviewPromptedKey] = useState('');
  const [showReviewSendInfoModal, setShowReviewSendInfoModal] = useState(false);
  // Clickable-chip answers for the coach: { [question_id]: { option, label } }, plus the base
  // message captured when the review arrived (so the built draft is stable as chips toggle).
  const [coachAnswers, setCoachAnswers] = useState({});
  const [coachBaseMessage, setCoachBaseMessage] = useState('');
  const ownerOnboardingStorageKey = currentUserId
    ? `${OWNER_ONBOARDING_DISMISS_KEY}:${currentUserId}`
    : OWNER_ONBOARDING_DISMISS_KEY;
  const [ownerOnboardingDismissed, setOwnerOnboardingDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(ownerOnboardingStorageKey) === 'true';
  });
  const lastAutoReviewKeyRef = useRef('');

  useEffect(() => {
    favoriteShopIdsRef.current = favoriteShopIds;
  }, [favoriteShopIds]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setOwnerOnboardingDismissed(localStorage.getItem(ownerOnboardingStorageKey) === 'true');
  }, [ownerOnboardingStorageKey]);

  const headers = { Authorization: `Bearer ${token}` };

  const clearStatusTimer = () => {
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = null;
    }
  };

  const showStatus = (type, message, autoClearMs = 0) => {
    clearStatusTimer();
    setStatus({ type, message });
    if (autoClearMs > 0) {
      statusTimeoutRef.current = setTimeout(() => {
        setStatus({ type: '', message: '' });
        statusTimeoutRef.current = null;
      }, autoClearMs);
    }
  };

  const clearSuccessStatusOnNewRequest = () => {
    if (status.type === 'success') {
      clearStatusTimer();
      setStatus({ type: '', message: '' });
    }
  };

  const resetRequestForm = () => {
    setSelectedMake('');
    setSelectedYear('');
    setSelectedModel('');
    setSelectedTrim('');
    setSelectedDescription('');
    setOtherMake('');
    setOtherYear('');
    setOtherModel('');
    setOtherTrim('');
    setOtherDescription('');
    setZipCode('');
    setSelectedRadius('5');
    setUserMessage('');
    setSelectedShopIds([]);
    setShops([]);
    setHasSearched(false);
    setZipSearchCenter(null);
    setMapError('');
    setFilterSpecialist(true);
    setFilterGeneral(true);
    setFilterFavoritesOnly(false);
    setRequestReview(null);
    setReviewVisible(false);
    setReviewError('');
    setReviewPromptedKey('');
    lastAutoReviewKeyRef.current = '';
  };


  // Utility for all fetch calls to auto logout on 401/403
  async function fetchWithAuth(url, options = {}) {
    const res = await fetch(url, options);
    if (res.status === 401 || res.status === 403) {
      if (onSessionExpired) onSessionExpired("/login");
      else if (onLogout) onLogout();
      throw new Error('Session expired');
    }
    return res;
  }

  useEffect(() => {
    return () => clearStatusTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load makes
  useEffect(() => {
    fetchWithAuth(`${API_BASE}/makes`)
      .then(res => res.json())
      .then(setMakes)
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Load years when make changes (only if make is from list)
  useEffect(() => {
    if (selectedMake && selectedMake !== OTHER) {
      fetchWithAuth(`${API_BASE}/years?make=${encodeURIComponent(selectedMake)}`)
        .then(res => res.json())
        .then(setYears)
        .catch(() => {});
    } else {
      setYears([]);
    }

    // reset downstream
    setModels([]);
    setTrims([]);
    setDescriptions([]);
    setSelectedYear('');
    setSelectedModel('');
    setSelectedTrim('');
    setSelectedDescription('');

    // clear "other" fields downstream
    setOtherYear('');
    setOtherModel('');
    setOtherTrim('');
    setOtherDescription('');
    // eslint-disable-next-line
  }, [selectedMake]);

  // Load models when year changes (only if make/year are from list)
  useEffect(() => {
    if (selectedMake && selectedMake !== OTHER && selectedYear && selectedYear !== OTHER) {
      fetchWithAuth(`${API_BASE}/models?make=${encodeURIComponent(selectedMake)}&year=${selectedYear}`)
        .then(res => res.json())
        .then(setModels)
        .catch(() => {});
    } else {
      setModels([]);
    }

    setTrims([]);
    setDescriptions([]);
    setSelectedModel('');
    setSelectedTrim('');
    setSelectedDescription('');

    setOtherModel('');
    setOtherTrim('');
    setOtherDescription('');
    // eslint-disable-next-line
  }, [selectedYear]);

  // Load trims when model changes (only if model is from list)
  useEffect(() => {
    if (
      selectedMake &&
      selectedMake !== OTHER &&
      selectedYear &&
      selectedYear !== OTHER &&
      selectedModel &&
      selectedModel !== OTHER
    ) {
      fetchWithAuth(
        `${API_BASE}/trims?make=${encodeURIComponent(selectedMake)}&year=${selectedYear}&model=${encodeURIComponent(
          selectedModel
        )}`
      )
        .then(res => res.json())
        .then(setTrims)
        .catch(() => {});
    } else {
      setTrims([]);
    }

    setDescriptions([]);
    setSelectedTrim('');
    setSelectedDescription('');

    setOtherTrim('');
    setOtherDescription('');
    // eslint-disable-next-line
  }, [selectedModel]);

  // Load descriptions when trim changes (only if trim is from list)
  useEffect(() => {
    if (
      selectedMake &&
      selectedMake !== OTHER &&
      selectedYear &&
      selectedYear !== OTHER &&
      selectedModel &&
      selectedModel !== OTHER &&
      selectedTrim &&
      selectedTrim !== OTHER
    ) {
      fetchWithAuth(
        `${API_BASE}/descriptions?make=${encodeURIComponent(selectedMake)}&year=${selectedYear}&model=${encodeURIComponent(
          selectedModel
        )}&trim=${encodeURIComponent(selectedTrim)}`
      )
        .then(res => res.json())
        .then(setDescriptions)
        .catch(() => {});
    } else {
      setDescriptions([]);
    }

    setSelectedDescription('');
    setOtherDescription('');
    // eslint-disable-next-line
  }, [selectedTrim]);

  // ✅ Build content string once, used for any send
  const buildMessageContent = () =>
    `
[Request]
Make: ${effectiveMake}
Year: ${effectiveYear}
Model: ${effectiveModel}
Trim: ${effectiveTrim}
Description: ${effectiveDescription}
ZIP: ${zipCode}
Message: ${userMessage}
  `.trim();

  const buildReviewPayload = () => ({
    make: effectiveMake,
    year: effectiveYear,
    model: effectiveModel,
    trim: effectiveTrim,
    description: effectiveDescription,
    zip_code: zipCode,
    message: userMessage,
  });

  const getReviewKey = () => JSON.stringify(buildReviewPayload());

  // Deterministic, bracket-free draft built from the base message + the chips the owner clicked,
  // in question order: "<base>. Details: grinding or scraping; while braking; rear of vehicle."
  const buildCoachDraft = () => {
    const base = (coachBaseMessage || '').trim();
    // The options are already self-descriptive phrases ("grinding or scraping", "while braking"),
    // so list them as-is - no "label:" prefix, which read as noise.
    const chosen = (requestReview?.missing_details || [])
      .map((it) => coachAnswers[it.id])
      .filter((a) => a && a.option)
      .map((a) => String(a.option).toLowerCase());
    if (!chosen.length) return base;
    const sep = base && !/[.!?]$/.test(base) ? '.' : '';
    return `${base}${sep} Details: ${chosen.join('; ')}.`;
  };

  const toggleCoachAnswer = (item, option) => {
    setCoachAnswers((prev) => {
      const current = prev[item.id];
      if (current && current.option === option) {
        const next = { ...prev };
        delete next[item.id];
        return next;
      }
      return { ...prev, [item.id]: { option } };
    });
  };

  const reviewRequestMessage = async ({ automatic = false } = {}) => {
    const trimmedMessage = userMessage.trim();
    if (!trimmedMessage || trimmedMessage.length < 8) {
      if (!automatic) setReviewError('Please enter a little more detail before reviewing.');
      return null;
    }

    const reviewKey = getReviewKey();
    if (automatic && lastAutoReviewKeyRef.current === reviewKey) return requestReview;

    setReviewLoading(true);
    setReviewError('');

    // Bound the wait so a slow or unavailable coach never blocks the user: abort after the
    // timeout, which closes the reviewing popup and lets them send.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REVIEW_TIMEOUT_MS);

    try {
      const res = await fetchWithAuth(`${API_BASE}/messages/request-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify(buildReviewPayload()),
        signal: controller.signal,
      });

      if (!res.ok) throw new Error('Unable to review this request right now.');

      const data = await res.json();
      setRequestReview(data);
      // Fresh review -> start chip selections over, and remember the base the built draft extends.
      setCoachAnswers({});
      setCoachBaseMessage((data.suggested_draft || userMessage || '').trim());
      setReviewVisible(!automatic || data.clarification_needed);
      lastAutoReviewKeyRef.current = reviewKey;
      if (!automatic && data.clarification_needed) {
        // Require explicit acknowledgement (add details or "Send as-is")
        // after each manual review that still needs clarification.
        setReviewPromptedKey('');
      }
      return data;
    } catch (err) {
      // The coach is optional - on timeout/abort/failure, surface a calm note and never block
      // sending. Returning null lets the send flow proceed as if no review was needed.
      if (!automatic) {
        setReviewError('The request coach is unavailable right now. You can still send your request.');
      }
      return null;
    } finally {
      clearTimeout(timeoutId);
      setReviewLoading(false);
    }
  };

  useEffect(() => {
    // Review is on demand only - it runs when the user clicks "Review my request" or "Send",
    // never automatically while typing. This keeps the coach from interrupting composition and
    // avoids spending tokens on every pause. Here we only clear a now-stale review; we never call
    // the AI. The send/manual review keys still re-review an edited message on the next action.
    const trimmedMessage = userMessage.trim();

    setReviewError('');
    if (!trimmedMessage || trimmedMessage.length < 8) {
      setRequestReview(null);
      setReviewVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMessage, effectiveMake, effectiveYear, effectiveModel, effectiveTrim, effectiveDescription, zipCode]);

  useEffect(() => {
    if (!reviewVisible || !requestReview || !requestCoachRef.current) return;
    requestCoachRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [reviewVisible, requestReview]);

  useEffect(() => {
    // After a send attempt, bring the status banner into view - it sits at the bottom of a
    // long form, so otherwise the user may not see the success (with its "View Messages"
    // button) or the error explaining what went wrong.
    if ((status.type === 'success' || status.type === 'error') && statusRef.current) {
      statusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [status]);

  // ---- Shop search / selection ----
  useEffect(() => {
    fetchWithAuth(`${API_BASE}/users/favorite-shops`, { headers })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const ids = Array.isArray(data) ? data.map((x) => x.shop_user_id).filter(Boolean) : [];
        setFavoriteShopIds(ids);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const trimmedZip = zipCode.trim();
    if (!trimmedZip || !selectedRadius) {
      setHasSearched(false);
      setShops([]);
      setSearchDiagnostics(null);
      setSelectedShopIds([]);
      setLoadingShops(false);
      return undefined;
    }

    setHasSearched(true);
    setLoadingShops(true);
    setSearchDiagnostics(null);

    const controller = new AbortController();
    const debounce = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          zip_code: trimmedZip,
          radius_miles: selectedRadius,
        });
        if (effectiveMake) {
          params.append('make', effectiveMake);
        }

        const res = await fetchWithAuth(`${API_BASE}/messages/shops/search?${params.toString()}`, {
          headers,
          signal: controller.signal,
        });

        if (!res.ok) {
          showStatus('error', 'Failed to load shops. Please try again.');
          setShops([]);
          setSearchDiagnostics(null);
          return;
        }

        const data = await res.json();
        const nextShops = Array.isArray(data) ? data : [];
        setShops(nextShops);
        setSelectedShopIds((prev) => prev.filter((id) => nextShops.some((shop) => shop.id === id)));

        try {
          const diagnosticsRes = await fetchWithAuth(`${API_BASE}/messages/shops/search/diagnostics?${params.toString()}`, {
            headers,
            signal: controller.signal,
          });
          if (diagnosticsRes.ok) {
            const diagnosticsData = await diagnosticsRes.json();
            setSearchDiagnostics(diagnosticsData && !Array.isArray(diagnosticsData) ? diagnosticsData : null);
          }
        } catch {
          if (!controller.signal.aborted) setSearchDiagnostics(null);
        }

        if (status.type === 'error') {
          setStatus({ type: '', message: '' });
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        showStatus('error', 'Session expired, please log in again.');
        setShops([]);
        setSearchDiagnostics(null);
      } finally {
        if (!controller.signal.aborted) {
          setLoadingShops(false);
        }
      }
    }, 300);

    return () => {
      controller.abort();
      clearTimeout(debounce);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipCode, selectedRadius, effectiveMake, token]);

  const handleToggleShop = (shopId) => {
    setSelectedShopIds((prev) =>
      prev.includes(shopId) ? prev.filter((id) => id !== shopId) : [...prev, shopId]
    );
  };

  const handleSelectAllVisible = (visible) => {
    const allIds = visible.map((s) => s.id);
    setSelectedShopIds(allIds);
  };

  const handleClearSelected = () => {
    setSelectedShopIds([]);
  };

  const toggleFavoriteShop = async (shopId, isFavoriteOverride = null) => {
    const isFavorite = typeof isFavoriteOverride === 'boolean' ? isFavoriteOverride : favoriteShopIds.includes(shopId);
    try {
      const res = await fetchWithAuth(`${API_BASE}/users/favorite-shops${isFavorite ? `/${shopId}` : ''}`, {
        method: isFavorite ? 'DELETE' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: isFavorite ? undefined : JSON.stringify({ shop_user_id: shopId }),
      });
      if (!res.ok) {
        showStatus('error', 'Failed to update favorite shop.');
        return;
      }
      setFavoriteShopIds((prev) =>
        isFavorite ? prev.filter((id) => id !== shopId) : (prev.includes(shopId) ? prev : [...prev, shopId])
      );
      return !isFavorite;
    } catch {
      showStatus('error', 'Failed to update favorite shop.');
      return isFavorite;
    }
  };

  // `shopIdsOverride` exists because setSelectedShopIds is async: the "Add it and send" path below
  // both updates the UI and re-enters here, and without an explicit list the re-entry would read
  // the pre-click selection and send WITHOUT the shop it just promised to add.
  const handleSendToSelected = async (shopIdsOverride) => {
    if (sendingRequest) return;
    setStatus({ type: '', message: '' });

    const shopIds = shopIdsOverride ?? selectedShopIds;

    if (!shopIds.length) {
      showStatus('error', 'Please select at least one shop to send your request to.');
      return;
    }

    if (!userMessage.trim()) {
      showStatus('error', 'Please enter a message before sending.');
      return;
    }

    if (!zipCode) {
      showStatus('error', 'Please enter a ZIP code.');
      return;
    }

    if (requestAttachmentState.uploading) {
      showStatus('error', 'Please wait for your attachments to finish uploading.');
      return;
    }

    // Demo only, and BEFORE the single-shop question on purpose: only the visitor's own shop
    // replies automatically, so sending without it produces a request that nothing ever answers -
    // which reads as a broken product rather than a demo. Taking "Add it and send" also takes the
    // selection from 1 to 2 shops, which dissolves the single-shop question below entirely; asking
    // in the other order would put two dialogs on one click.
    if (ownDemoShop && !shopIds.includes(ownDemoShop.id) && !demoShopAckRef.current) {
      setShowDemoShopConfirm(true);
      return;
    }

    // Ask about the single-shop choice BEFORE the request coach runs, so the shop-count
    // question always comes first and the coach's "Reviewing..." only ever runs once
    // (on the acknowledged re-entry).
    if (shopIds.length === 1 && !singleShopAckRef.current) {
      setShowSingleShopConfirm(true);
      return;
    }

    const reviewKey = getReviewKey();
    if (reviewPromptedKey !== reviewKey) {
      const latestReview = requestReview && lastAutoReviewKeyRef.current === reviewKey
        ? requestReview
        : await reviewRequestMessage({ automatic: false });

      if (latestReview && latestReview.clarification_needed) {
        setRequestReview(latestReview);
        setReviewVisible(true);
        setShowReviewSendInfoModal(true);
        showStatus('info', 'A few details could help shops respond faster. Tap the suggestions below to add them, then Send - or click Send as-is.');
        return;
      }
    }

    const content = buildMessageContent();

    setSendingRequest(true);
    try {
      const res = await fetchWithAuth(`${API_BASE}/messages/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          content,
          zip_code: zipCode,
          radius_miles: parseInt(selectedRadius, 10),
          shop_ids: shopIds,
          attachment_ids: requestAttachmentState.attachmentIds,
        }),
      });

      if (res.ok) {
        resetRequestForm();
        requestAttachmentsRef.current?.reset();
        showStatus('success', 'Your request was sent! Shops reply in your Messages inbox - check there for replies, keep chatting, and start a work order once you pick a shop.');
      } else {
        showStatus('error', 'Failed to send. Please try again.');
      }
    } catch (err) {
      showStatus('error', 'Session expired, please log in again.');
    } finally {
      setSendingRequest(false);
      // The attempt reached an actual send - the next one should ask about shop count again.
      singleShopAckRef.current = false;
      demoShopAckRef.current = false;
    }
  };

  // Adds the visitor's own demo shop and sends in one step. `next` is passed through explicitly -
  // see the note on shopIdsOverride above; setSelectedShopIds only updates what is on screen.
  const confirmAddDemoShop = async () => {
    setShowDemoShopConfirm(false);
    if (!ownDemoShop) return;
    const next = [...selectedShopIds, ownDemoShop.id];
    setSelectedShopIds(next);
    await handleSendToSelected(next);
  };

  // Closes WITHOUT sending, matching the single-shop dialog's cancel. ConfirmDialog focuses cancel
  // on open and maps Escape to it, so anything that sent from here would let a stray Escape fire a
  // request the visitor never asked for. The ack means the next Send goes straight through.
  const dismissDemoShopConfirm = () => {
    demoShopAckRef.current = true;
    setShowDemoShopConfirm(false);
  };

  const confirmSingleShopSend = async () => {
    setShowSingleShopConfirm(false);
    // Stays acknowledged through this whole attempt (including a request-coach round trip,
    // so the dialog is not asked twice for one request). Reset after an actual send, or
    // when the shop selection changes.
    singleShopAckRef.current = true;
    await handleSendToSelected();
  };

  // Apply specialist/general filters client-side
  const visibleShops = useMemo(() => {
    const filtered = shops.filter((shop) => {
      const specialistMatch = filterSpecialist && shop.is_specialist_for_make === true;
      const generalMatch = filterGeneral && shop.does_general_repair === true;
      const favoriteMatch = !filterFavoritesOnly || favoriteShopIds.includes(shop.id);

      if (mobileFilter === "only" && !shop.is_mobile_mechanic) return false;
      if (mobileFilter === "hide" && shop.is_mobile_mechanic) return false;
      if (!shopOffersService(shop, filterService)) return false;
      if (!filterSpecialist && !filterGeneral) return false;
      return (specialistMatch || generalMatch) && favoriteMatch;
    });

    // Demo only: float the visitor's own shop to the top. It is the one shop that answers
    // automatically, so burying it behind a page of seeded fixtures is how a visitor ends up
    // sending a request that nothing ever replies to. Sort is stable, so every other shop keeps
    // its fit-score order, and this is what gets the shop onto page 1 of the paged list.
    if (!demoMode) return filtered;
    return [...filtered].sort((a, b) => (b.is_own_demo_shop ? 1 : 0) - (a.is_own_demo_shop ? 1 : 0));
  }, [shops, filterSpecialist, filterGeneral, filterFavoritesOnly, filterService, favoriteShopIds, mobileFilter, demoMode]);

  // The visitor's own ephemeral demo shop, resolved from the FETCHED list rather than the filtered
  // one: if a filter or a distant ZIP has hidden it there is still an id to add, and the nudge
  // below is only worth showing when there is something it can actually add.
  const ownDemoShop = useMemo(
    () => (demoMode ? shops.find((shop) => shop.is_own_demo_shop) || null : null),
    [demoMode, shops],
  );
  const ownDemoShopSelected = !!ownDemoShop && selectedShopIds.includes(ownDemoShop.id);

  // Page the filtered list (5 per page) instead of a cramped scroll box.
  const pageCount = Math.max(1, Math.ceil(visibleShops.length / SHOPS_PER_PAGE));
  const pageStart = shopPage * SHOPS_PER_PAGE;
  const pagedShops = useMemo(
    () => visibleShops.slice(pageStart, pageStart + SHOPS_PER_PAGE),
    [visibleShops, pageStart],
  );

  // Start back at page 1 when a new search loads (ZIP / radius / make changed).
  useEffect(() => {
    setShopPage(0);
  }, [zipCode, selectedRadius, effectiveMake]);

  // Keep the current page in range when filters shrink the list.
  useEffect(() => {
    setShopPage((prev) => Math.min(prev, pageCount - 1));
  }, [pageCount]);

  const searchRecovery = useMemo(() => {
    if (!hasSearched || loadingShops) return null;
    return buildShopSearchRecovery({
      shops,
      visibleShops,
      radiusMiles: selectedRadius,
      radiusOptions: RADIUS_OPTIONS,
      make: effectiveMake,
      zipCode,
      filterSpecialist,
      filterGeneral,
      filterFavoritesOnly,
      diagnostics: searchDiagnostics,
    });
  }, [effectiveMake, filterFavoritesOnly, filterGeneral, filterSpecialist, hasSearched, loadingShops, searchDiagnostics, selectedRadius, shops, visibleShops, zipCode]);

  useEffect(() => {
    if (!mapToken || !hasSearched || !mapContainerRef.current || mapRef.current) return undefined;

    let active = true;
    loadMapboxAssets()
      .then((mapboxgl) => {
        if (!active || !mapContainerRef.current || mapRef.current) return;
        mapboxRef.current = mapboxgl;
        mapboxgl.accessToken = mapToken;
        const map = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: mapStyleUrl(),
          center: DEFAULT_CENTER,
          zoom: 3,
        });
        map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        if (typeof map.on === 'function') {
          map.on('load', () => {
            if (active) setMapReady(true);
          });
        } else if (active) {
          setMapReady(true);
        }
        mapRef.current = map;
      })
      .catch(() => {
        if (active) setMapError('Failed to load Mapbox.');
      });

    return () => {
      active = false;
      clustererRef.current?.destroy();
      clustererRef.current = null;
      if (searchCenterMarkerRef.current) {
        searchCenterMarkerRef.current.remove();
        searchCenterMarkerRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      setMapReady(false);
    };
  }, [mapToken, hasSearched]);

  useEffect(() => {
    if (!hasSearched || !zipCode.trim() || !mapToken) {
      setZipSearchCenter(null);
      return undefined;
    }

    const backendCenter = searchDiagnostics?.search_center;
    if (backendCenter && Number.isFinite(backendCenter.longitude) && Number.isFinite(backendCenter.latitude)) {
      setZipSearchCenter([backendCenter.longitude, backendCenter.latitude]);
      return undefined;
    }
    if (searchDiagnostics?.zip_found === false) {
      setZipSearchCenter(null);
      return undefined;
    }

    const controller = new AbortController();
    const loadZipCenter = async () => {
      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            zipCode.trim()
          )}.json?country=us&types=postcode&limit=1&access_token=${encodeURIComponent(mapToken)}`,
          { signal: controller.signal }
        );
        if (!res.ok) {
          setZipSearchCenter(null);
          return;
        }
        const data = await res.json();
        const center = Array.isArray(data?.features) && data.features[0]?.center;
        if (
          Array.isArray(center) &&
          Number.isFinite(center[0]) &&
          Number.isFinite(center[1])
        ) {
          setZipSearchCenter([center[0], center[1]]);
        } else {
          setZipSearchCenter(null);
        }
      } catch {
        if (!controller.signal.aborted) {
          setZipSearchCenter(null);
        }
      }
    };

    loadZipCenter();
    return () => controller.abort();
  }, [hasSearched, mapToken, searchDiagnostics, zipCode]);

  useEffect(() => {
    const map = mapRef.current;
    const mapboxgl = mapboxRef.current;
    if (!map || !mapboxgl || !mapReady || !hasSearched) return;

    if (!clustererRef.current) {
      clustererRef.current = createShopClusterer({
        map,
        mapboxgl,
        onRender: (individualShops) => renderMobileRadiusLayers(map, individualShops),
      });
    }
    if (searchCenterMarkerRef.current) {
      searchCenterMarkerRef.current.remove();
      searchCenterMarkerRef.current = null;
    }

    if (map.getLayer(RADIUS_FILL_LAYER_ID)) map.removeLayer(RADIUS_FILL_LAYER_ID);
    if (map.getLayer(RADIUS_OUTLINE_LAYER_ID)) map.removeLayer(RADIUS_OUTLINE_LAYER_ID);
    if (map.getSource(RADIUS_SOURCE_ID)) map.removeSource(RADIUS_SOURCE_ID);

    const bounds = new mapboxgl.LngLatBounds();
    let hasBounds = false;

    if (zipSearchCenter && Number.isFinite(Number(selectedRadius))) {
      const radiusFeature = buildCircleGeoJson(zipSearchCenter, Number(selectedRadius));
      map.addSource(RADIUS_SOURCE_ID, { type: 'geojson', data: radiusFeature });
      map.addLayer({
        id: RADIUS_FILL_LAYER_ID,
        type: 'fill',
        source: RADIUS_SOURCE_ID,
        paint: {
          'fill-color': '#2563eb',
          'fill-opacity': 0.14,
        },
      });
      map.addLayer({
        id: RADIUS_OUTLINE_LAYER_ID,
        type: 'line',
        source: RADIUS_SOURCE_ID,
        paint: {
          'line-color': '#1d4ed8',
          'line-width': 2,
        },
      });

      searchCenterMarkerRef.current = new mapboxgl.Marker({ color: '#1d4ed8' })
        .setLngLat(zipSearchCenter)
        .setPopup(new mapboxgl.Popup({ offset: 18 }).setText(`Search center: ${zipCode.trim()}`))
        .addTo(map);

    }

    const validShops = visibleShops.filter((shop) => Number.isFinite(shop.longitude) && Number.isFinite(shop.latitude));
    const markerShops = Array.from(new Map(
      validShops.map((shop, index) => [
        String(shop.shop_name || shop.id || `${shop.latitude}:${shop.longitude}:${index}`).trim().toLowerCase(),
        shop,
      ])
    ).values());

    // Builds one exact-anchor pin (with popup + favorite wiring) for an unclustered shop.
    const buildShopMarker = (shop) => {
      const popupView = buildShopPopupViewModel(shop, {
        effectiveMake,
        mapsUrl: getShopGoogleMapsUrl(shop),
        isFavorite: favoriteShopIds.includes(shop.id),
      });
      const marker = createShopMapMarker(mapboxgl, {
        label: shop.shop_name,
        variant: shop.is_mobile_mechanic ? "mobile" : "shop",
      })
        .setLngLat([shop.longitude, shop.latitude])
        .setPopup(
          new mapboxgl.Popup({ offset: 18, closeOnClick: false, className: 'shop-map-popup shop-map-popup--flush-close' }).setHTML(
            buildShopMapPopupHtml({
              shop,
              mapsUrl: popupView.mapsUrl,
              badges: popupView.badges,
              effectiveMake,
              trustSummary: popupView.trustSummary,
              showFavoriteButton: true,
              isFavorite: popupView.isFavorite,
            })
          )
        )
        .addTo(map);
      marker.getPopup().on('open', () => {
        const popupEl = marker.getPopup()?.getElement();
        const favoriteButton = popupEl?.querySelector?.('[data-favorite-toggle="true"]');
        if (favoriteButton) {
          const stopEvent = (event) => {
            event.preventDefault();
            event.stopPropagation();
          };
          favoriteButton.addEventListener('mousedown', stopEvent);
          favoriteButton.addEventListener('mouseup', stopEvent);
          favoriteButton.addEventListener('click', (event) => {
            stopEvent(event);
            const iconEl = favoriteButton.querySelector('i');
            const isFavoriteNow = !!iconEl && iconEl.className.includes('fi-sr-heart');
            toggleFavoriteShop(shop.id, isFavoriteNow).then((nextIsFavorite) => {
              if (!iconEl) return;
              iconEl.className = nextIsFavorite ? 'fi fi-sr-heart' : 'fi fi-rs-heart fi-rr-heart';
              favoriteButton.style.color = nextIsFavorite ? '#dc2626' : '#374151';
              favoriteButton.title = nextIsFavorite ? 'Unfavorite shop' : 'Favorite shop';
              favoriteButton.setAttribute('aria-label', nextIsFavorite ? 'Unfavorite shop' : 'Favorite shop');
            });
          });
        }
      });
      return marker;
    };

    // Clustering owns the shop pins + mobile-radius circles from here.
    clustererRef.current.setShops(markerShops, buildShopMarker);

    const fitShops = Array.from(new Map(
      shops
        .filter((shop) => Number.isFinite(shop.longitude) && Number.isFinite(shop.latitude))
        .map((shop, index) => [
          String(shop.shop_name || shop.id || `${shop.latitude}:${shop.longitude}:${index}`).trim().toLowerCase(),
          shop,
        ])
    ).values());
    fitShops.forEach((shop) => {
      bounds.extend([shop.longitude, shop.latitude]);
      hasBounds = true;
    });

    if (zipSearchCenter) {
      bounds.extend(zipSearchCenter);
      hasBounds = true;
    }

    if (!hasBounds) {
      map.flyTo({ center: DEFAULT_CENTER, zoom: 3 });
      return undefined;
    }

    const fitKey = JSON.stringify({
      shops: fitShops.map((shop) => [shop.id, shop.longitude, shop.latitude]),
      zipSearchCenter,
      zipCode: zipCode.trim(),
      selectedRadius,
    });
    if (lastMapFitKeyRef.current !== fitKey) {
      lastMapFitKeyRef.current = fitKey;
      map.fitBounds(bounds, { padding: 40, maxZoom: 11 });
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSearched, mapReady, selectedRadius, shops, visibleShops, zipCode, zipSearchCenter, effectiveMake, favoriteShopIds]);

  const makeLabelForUI = effectiveMake || 'this make';
  const ownerOnboardingSteps = [
    { label: 'Choose vehicle details', done: Boolean(effectiveMake) },
    { label: 'Describe the issue clearly', done: userMessage.trim().length >= 8 },
    { label: 'Search nearby shops', done: Boolean(zipCode.trim()) && hasSearched },
    { label: 'Select shops to contact', done: selectedShopIds.length > 0 },
  ];
  const ownerOnboardingCompleted = ownerOnboardingSteps.filter((step) => step.done).length;
  const showOwnerOnboarding = !isShopUser && !ownerOnboardingDismissed;

  function dismissOwnerOnboarding() {
    setOwnerOnboardingDismissed(true);
    if (typeof window !== 'undefined') {
      localStorage.setItem(ownerOnboardingStorageKey, 'true');
    }
  }

  function startOwnerOnboarding() {
    firstVehicleFieldRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    firstVehicleFieldRef.current?.focus?.();
  }

  return (
    <form onSubmit={(e) => e.preventDefault()} className="max-w-6xl mx-auto space-y-6 p-4">

      {showOwnerOnboarding ? (
        <div className="border border-blue-200 dark:border-blue-900 rounded-xl bg-blue-50 dark:bg-blue-950/40 shadow-sm p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">First request guide</p>
              <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">Send your first request in about 2 minutes</h3>
              <p className="mt-2 text-sm text-blue-950 dark:text-blue-200 leading-relaxed">
                We’ll help you describe the issue, compare nearby shops, and send one request to a few good-fit shops.
              </p>
            </div>
            <button
              type="button"
              onClick={dismissOwnerOnboarding}
              className="text-xs font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-900 whitespace-nowrap"
            >
              Don’t show again
            </button>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ownerOnboardingSteps.map((step) => (
              <div key={step.label} className="flex items-center gap-2 text-sm text-blue-950 dark:text-blue-200">
                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${step.done ? 'bg-green-600 text-white' : 'bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-900 text-blue-700 dark:text-blue-300'}`}>
                  {step.done ? '✓' : '○'}
                </span>
                <span>{step.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button type="button" onClick={startOwnerOnboarding} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
              Start request
            </button>
            <span className="text-xs text-blue-900 dark:text-blue-200">{ownerOnboardingCompleted} of {ownerOnboardingSteps.length} steps complete</span>
          </div>
        </div>
      ) : null}

      {/* ✅ Help / instructions card */}
      <div className="border rounded-xl bg-white dark:bg-gray-800 shadow-sm p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Send a request to local shops</h3>

        <p className="mt-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
          Please select your vehicle details. If you’re unsure about something, choose the closest match. If you don’t see
          the right option, choose <span className="font-semibold">Other</span> and type what you know.
        </p>

        <p className="mt-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
          The more specific you are (symptoms, warning lights, noises, leaks, or what you want done), the faster shops can
          understand your request and respond with accurate guidance.
        </p>

        {/* Multi-shop value prop: styled as a permanent product-feature card (blue accent,
            icon tile, headline) - not an alert/notice, which users tend to skip. */}
        <div className="mt-3 flex items-start gap-3 rounded-xl border border-l-4 border-blue-200 dark:border-blue-800 border-l-blue-600 dark:border-l-blue-500 bg-blue-50 dark:bg-blue-950/40 p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300">
            <span className="material-symbols-outlined text-xl" aria-hidden="true">storefront</span>
          </div>
          <div>
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">One request - multiple shops</p>
            <p className="mt-0.5 text-sm text-blue-900 dark:text-blue-200 leading-relaxed">
              Send this same request to <span className="font-semibold">several nearby shops at once</span>, compare
              responses and quotes, and choose the best fit for your needs.
            </p>
          </div>
        </div>
      </div>

      {/* Vehicle selection block */}
      <div className="space-y-2">
        {/* Make */}
        <select
          ref={firstVehicleFieldRef}
          aria-label="Vehicle make"
          value={selectedMake}
          onChange={(e) => {
            clearSuccessStatusOnNewRequest();
            setSelectedMake(e.target.value);
            if (e.target.value !== OTHER) setOtherMake('');
          }}
          className="w-full p-2 border rounded"
        >
          <option value="">Select Make</option>
          {makes.map((make) => (
            <option key={make} value={make}>
              {make}
            </option>
          ))}
          <option value={OTHER}>Other…</option>
        </select>
        {selectedMake === OTHER && (
          <input
            type="text"
            value={otherMake}
            onChange={(e) => setOtherMake(e.target.value)}
            placeholder="Enter make (Other)"
            className="w-full p-2 border rounded"
          />
        )}

        {/* Year */}
        <select
          value={selectedYear}
          onChange={(e) => {
            clearSuccessStatusOnNewRequest();
            setSelectedYear(e.target.value);
            if (e.target.value !== OTHER) setOtherYear('');
          }}
          className="w-full p-2 border rounded"
          disabled={!selectedMake}
        >
          <option value="">Select Year</option>
          {years.map((year) => (
            <option key={String(year)} value={String(year)}>
              {String(year)}
            </option>
          ))}
          <option value={OTHER}>Other…</option>
        </select>
        {selectedYear === OTHER && (
          <input
            type="text"
            value={otherYear}
            onChange={(e) => setOtherYear(e.target.value)}
            placeholder="Enter year (Other)"
            className="w-full p-2 border rounded"
          />
        )}

        {/* Model */}
        <select
          value={selectedModel}
          onChange={(e) => {
            clearSuccessStatusOnNewRequest();
            setSelectedModel(e.target.value);
            if (e.target.value !== OTHER) setOtherModel('');
          }}
          className="w-full p-2 border rounded"
          disabled={!selectedYear}
        >
          <option value="">Select Model</option>
          {models.map((model) => (
            <option key={model} value={model}>
              {model}
            </option>
          ))}
          <option value={OTHER}>Other…</option>
        </select>
        {selectedModel === OTHER && (
          <input
            type="text"
            value={otherModel}
            onChange={(e) => setOtherModel(e.target.value)}
            placeholder="Enter model (Other)"
            className="w-full p-2 border rounded"
          />
        )}

        {/* Trim */}
        <select
          value={selectedTrim}
          onChange={(e) => {
            clearSuccessStatusOnNewRequest();
            setSelectedTrim(e.target.value);
            if (e.target.value !== OTHER) setOtherTrim('');
          }}
          className="w-full p-2 border rounded"
          disabled={!selectedModel}
        >
          <option value="">Select Trim</option>
          {trims.map((trim) => (
            <option key={trim} value={trim}>
              {trim}
            </option>
          ))}
          <option value={OTHER}>Other…</option>
        </select>
        {selectedTrim === OTHER && (
          <input
            type="text"
            value={otherTrim}
            onChange={(e) => setOtherTrim(e.target.value)}
            placeholder="Enter trim (Other)"
            className="w-full p-2 border rounded"
          />
        )}

        {/* Description */}
        <select
          value={selectedDescription}
          onChange={(e) => {
            clearSuccessStatusOnNewRequest();
            setSelectedDescription(e.target.value);
            if (e.target.value !== OTHER) setOtherDescription('');
          }}
          className="w-full p-2 border rounded"
          disabled={!selectedTrim}
        >
          <option value="">Select Description</option>
          {descriptions.map((desc, index) => (
            <option key={index} value={desc}>
              {desc}
            </option>
          ))}
          <option value={OTHER}>Other…</option>
        </select>
        {selectedDescription === OTHER && (
          <input
            type="text"
            value={otherDescription}
            onChange={(e) => setOtherDescription(e.target.value)}
            placeholder="Enter description (Other)"
            className="w-full p-2 border rounded"
          />
        )}
      </div>

      {/* Location + radius (auto-updates shops + map) */}
      <div className="space-y-2">
        <input
          type="text"
          inputMode="numeric"
          maxLength={5}
          value={zipCode}
          onChange={(e) => {
            setZipTouched(true);
            setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5));
          }}
          placeholder="Enter ZIP code"
          aria-label="ZIP code"
          className="w-full p-2 border rounded"
        />
        {zipCode.length > 0 && zipCode.length < 5 && (
          <p className="text-xs text-amber-700 dark:text-amber-400">Enter a 5-digit ZIP code.</p>
        )}
        <UseMyLocationButton onZip={(z) => { setZipTouched(true); setZipCode(z); }} />

        <select
          value={selectedRadius}
          onChange={(e) => {
            clearSuccessStatusOnNewRequest();
            setSelectedRadius(e.target.value);
          }}
          aria-label="Search radius miles"
          className="w-full p-2 border rounded"
        >
          {RADIUS_OPTIONS.map((miles) => (
            <option key={miles} value={miles}>
              Search Radius: {miles} miles
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-600 dark:text-gray-300">
          Shops and map update automatically as you change ZIP, radius, or make.
        </p>
      </div>

      {loadingShops && hasSearched ? (
        <p className="text-xs text-gray-600 dark:text-gray-300">Updating shops and map…</p>
      ) : null}
      {hasSearched && !loadingShops && shops.length === 0 ? (
        <ShopSearchRecoveryPanel
          recovery={searchRecovery}
          onExpandRadius={(radius) => setSelectedRadius(radius)}
          onIncludeGeneral={() => setFilterGeneral(true)}
          onShowAll={() => setFilterFavoritesOnly(false)}
        />
      ) : null}

      {/* Shop filters + list */}
      {shops.length > 0 && (
        <div className="space-y-3 border rounded p-3">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <FilterChip active={filterSpecialist} onToggle={() => setFilterSpecialist(!filterSpecialist)}>
                Specializes in {makeLabelForUI}
              </FilterChip>
              <FilterChip active={filterGeneral} onToggle={() => setFilterGeneral(!filterGeneral)}>
                General auto repair
              </FilterChip>
              <FilterChip active={filterFavoritesOnly} onToggle={() => setFilterFavoritesOnly(!filterFavoritesOnly)}>
                Favorites only
              </FilterChip>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-300">
                <span>Mobile mechanics</span>
                <select
                  aria-label="Mobile mechanics"
                  value={mobileFilter}
                  onChange={(e) => setMobileFilter(e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="include">Include</option>
                  <option value="only">Only</option>
                  <option value="hide">Hide</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-300">
                <span>Service</span>
                <select
                  aria-label="Service (optional)"
                  value={filterService}
                  onChange={(e) => setFilterService(e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">Any service</option>
                  {SHOP_SERVICE_OPTIONS.map((svc) => (
                    <option key={svc} value={svc}>
                      {svc}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <button
                type="button"
                onClick={() => handleSelectAllVisible(visibleShops)}
                className="px-2 py-1 border rounded-xl"
              >
                Select all shown
              </button>
              <button
                type="button"
                onClick={handleClearSelected}
                className="px-2 py-1 border rounded-xl"
              >
                Clear selection
              </button>
            </div>
          </div>

          {searchRecovery && shops.length > 0 ? (
            <ShopSearchRecoveryPanel
              recovery={searchRecovery}
              onExpandRadius={(radius) => setSelectedRadius(radius)}
              onIncludeGeneral={() => setFilterGeneral(true)}
              onShowAll={() => setFilterFavoritesOnly(false)}
            />
          ) : null}

          {visibleShops.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">Adjust the search recovery options above or change your filters to show shops.</p>
          ) : (
            <div className="space-y-3">
              <ul className="space-y-2">
                {pagedShops.map((shop) => (
                  <li key={shop.id}>
                    <ShopMatchCard
                      shop={shop}
                      effectiveMake={effectiveMake}
                      mapsUrl={getShopGoogleMapsUrl(shop)}
                      selectable
                      selected={selectedShopIds.includes(shop.id)}
                      onToggleSelect={() => handleToggleShop(shop.id)}
                      showFavoriteButton
                      isFavorite={favoriteShopIds.includes(shop.id)}
                      onToggleFavorite={() => toggleFavoriteShop(shop.id)}
                    />
                  </li>
                ))}
              </ul>

              {pageCount > 1 ? (
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setShopPage((prev) => Math.max(0, prev - 1))}
                    disabled={shopPage === 0}
                    className="px-3 py-1.5 border rounded-xl text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-gray-600 dark:text-gray-300">
                    Showing {pageStart + 1}-{Math.min(pageStart + SHOPS_PER_PAGE, visibleShops.length)} of {visibleShops.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShopPage((prev) => Math.min(pageCount - 1, prev + 1))}
                    disabled={shopPage >= pageCount - 1}
                    className="px-3 py-1.5 border rounded-xl text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </div>
          )}

          <div className="rounded-lg border border-blue-100 bg-blue-50 dark:bg-blue-950/40 px-3 py-2 text-xs text-blue-950 dark:text-blue-200">
            New here? Select 2-4 good-fit shops when possible so you can compare response speed, specialties, and completed work order history.
            {/* Demo only, and only until it is acted on - a reminder that stays put after you have
                done the thing is just noise. Rendered inside this box rather than as a second
                panel so the page does not stack two blue cards. */}
            {ownDemoShop && !ownDemoShopSelected ? (
              <span className="mt-1 block font-semibold">
                Demo tip: include Your Demo Shop in your selection - it is the only shop here that replies automatically.
              </span>
            ) : null}
          </div>

          <p className="text-xs text-gray-600 dark:text-gray-300">Selected shops: {selectedShopIds.length}</p>
        </div>
      )}

      {hasSearched && (
        <div className="space-y-2 border rounded p-3 bg-white dark:bg-gray-800">
          <div>
            <p className="text-sm font-medium text-gray-800 dark:text-gray-100">Search area map</p>
            <p className="text-xs text-gray-600 dark:text-gray-300">
              Displays the ZIP search center and {selectedRadius}-mile radius with visible shops.
            </p>
          </div>
          {!mapToken ? (
            <div className="border rounded-lg px-3 py-2 text-sm border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-gray-900 text-yellow-900 dark:text-yellow-200">
              Map preview needs a Mapbox token. Set <code>REACT_APP_MAPBOX_TOKEN</code> in the frontend environment.
            </div>
          ) : mapError ? (
            <div className="border rounded-lg px-3 py-2 text-sm border-red-300 bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-300">{mapError}</div>
          ) : (
            <div className="border rounded overflow-hidden">
              <div ref={mapContainerRef} className="w-full h-[560px]" />
            </div>
          )}
          {mapToken && !mapError ? <MapLegend /> : null}
        </div>
      )}

      {/* Message + final send */}
      <div className="space-y-2">
        <textarea
          aria-label="Request message"
          value={userMessage}
          onChange={(e) => {
            clearSuccessStatusOnNewRequest();
            setUserMessage(e.target.value);
          }}
          placeholder="Describe the issue or what you’re looking for..."
          className="w-full p-2 border rounded"
          rows={4}
        />
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-2 text-xs text-gray-700 dark:text-gray-200">
          <span className="font-semibold">Tip:</span> Include symptoms, when it started, warning lights, whether the car is drivable, and how urgent it is.
        </div>

        <div className="rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 px-3 py-2">
          <RequestAttachments ref={requestAttachmentsRef} token={token} onChange={setRequestAttachmentState} />
        </div>

        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => reviewRequestMessage({ automatic: false })}
            className="border border-blue-600 text-blue-700 dark:text-blue-300 px-4 py-2 rounded-xl flex-1"
            disabled={reviewLoading || !userMessage.trim()}
          >
            {reviewLoading ? 'Reviewing…' : 'Review my request'}
          </button>
          <button
            type="button"
            // Wrapped, not passed by reference: React hands onClick the click event as the first
            // argument, which would arrive as shopIdsOverride and be used as the shop list.
            onClick={() => handleSendToSelected()}
            className="bg-blue-600 text-white px-4 py-2 rounded-xl flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={
              !shops.length ||
              reviewLoading ||
              sendingRequest ||
              !selectedShopIds.length ||
              !userMessage.trim()
            }
          >
            {sendingRequest ? 'Sending...' : 'Send Request to Selected Shop(s)'}
          </button>
        </div>

        <ConfirmDialog
          open={showSingleShopConfirm}
          title="Send to just one shop?"
          body="GearCheckAuto lets you send this same request to several shops at once, so you can compare quotes and response times before choosing. You can still send to just this one."
          confirmLabel="Send to 1 shop"
          cancelLabel="Add more shops"
          onConfirm={confirmSingleShopSend}
          onCancel={() => setShowSingleShopConfirm(false)}
        />

        <ConfirmDialog
          open={showDemoShopConfirm}
          title="Add Your Demo Shop to this request?"
          body="The other shops here are samples and will not write back. Your Demo Shop replies automatically, so adding it is what lets you see a real conversation - and you can answer yourself from the shop side."
          confirmLabel="Add it and send"
          cancelLabel="Not now"
          onConfirm={confirmAddDemoShop}
          onCancel={dismissDemoShopConfirm}
        />

        {reviewLoading && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            role="dialog"
            aria-modal="true"
            aria-label="Reviewing your request"
          >
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-6 shadow-xl flex flex-col items-center gap-3 text-center">
              <span
                className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-200 dark:border-blue-900 border-t-blue-600"
                aria-hidden="true"
              />
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Reviewing your request...</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                This usually takes a few seconds. This window closes automatically when the review is ready.
              </p>
            </div>
          </div>
        )}

        {reviewError && (
          <div className="border rounded-xl px-3 py-2 text-sm border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-gray-900 text-yellow-900 dark:text-yellow-200">
            {reviewError}
          </div>
        )}

        {!reviewLoading && requestReview && reviewVisible && (requestReview.clarification_needed || requestReview.suggested_draft) && (
          <div ref={requestCoachRef} className="border rounded-xl p-3 space-y-3 bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-900 text-sm text-blue-950 dark:text-blue-200">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold">Request coach</p>
                <p>{requestReview.summary}</p>
                <p className="text-xs text-blue-800 dark:text-blue-300 mt-1">Quality score: {requestReview.quality_score}/100</p>
              </div>
              {requestReview.send_ready && (
                <span className="text-xs rounded-full bg-green-100 dark:bg-green-950/40 text-green-800 dark:text-green-300 px-2 py-1">Looks ready</span>
              )}
            </div>

            {requestReview.safety_flags?.length > 0 && (
              <div className="border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-gray-900 text-yellow-950 dark:text-yellow-200 rounded-lg p-2">
                <p className="font-medium">Safety note</p>
                <ul className="list-disc pl-5">
                  {requestReview.safety_flags.map((flag, index) => (
                    <li key={index}>{flag}</li>
                  ))}
                </ul>
              </div>
            )}

            {requestReview.missing_details?.length > 0 && (
              <div className="space-y-2">
                <p className="font-medium">Tap the details that apply and we'll add them to your request:</p>
                {requestReview.missing_details.map((item) => (
                  <div key={item.id} className="bg-white dark:bg-gray-800 border rounded-lg p-2">
                    <p className="font-medium">{item.question}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.options.map((option) => {
                        const selected = coachAnswers[item.id]?.option === option;
                        return (
                          <button
                            type="button"
                            key={option}
                            onClick={() => toggleCoachAnswer(item, option)}
                            aria-pressed={selected}
                            className={`text-xs border rounded-full px-2 py-1 transition ${
                              selected
                                ? 'bg-blue-600 border-blue-600 text-white'
                                : 'bg-white dark:bg-gray-800 text-blue-900 dark:text-blue-200 border-blue-200 dark:border-blue-900 hover:bg-blue-50 dark:hover:bg-blue-950/40'
                            }`}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {coachBaseMessage && (
              <div className="space-y-2">
                <p className="font-medium">Your request will read:</p>
                <div className="bg-white dark:bg-gray-800 border rounded-lg p-2 text-gray-800 dark:text-gray-100 whitespace-pre-wrap">
                  {buildCoachDraft()}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    disabled={Object.keys(coachAnswers).length === 0}
                    className="bg-blue-600 text-white rounded-xl px-3 py-1.5 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => {
                      const draft = buildCoachDraft();
                      // Accept the built draft as already-reviewed so Send just sends (no re-review).
                      const draftKey = JSON.stringify({ ...buildReviewPayload(), message: draft });
                      setUserMessage(draft);
                      lastAutoReviewKeyRef.current = draftKey;
                      setReviewPromptedKey(draftKey);
                      setReviewVisible(false);
                      setShowReviewSendInfoModal(false);
                    }}
                  >
                    Use these details
                  </button>
                  <button
                    type="button"
                    className="border border-blue-600 text-blue-700 dark:text-blue-300 rounded-xl px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-950/40"
                    onClick={() => {
                      setReviewPromptedKey(getReviewKey());
                      setReviewVisible(false);
                      setShowReviewSendInfoModal(false);
                    }}
                  >
                    Send as-is
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {showReviewSendInfoModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Review suggested updates before sending</p>
              <div className="mt-2 text-sm text-gray-700 dark:text-gray-200">
                <p>Tap the suggested details to add them, or click</p>
                <p className="mt-1">
                  <span className="whitespace-nowrap font-semibold text-blue-700 dark:text-blue-300">Send as-is</span>{' '}
                  in the request coach first.
                </p>
              </div>
              <div className="mt-4 flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => setShowReviewSendInfoModal(false)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {status.message && (
        <div
          ref={statusRef}
          className={`border rounded-xl px-3 py-2 text-sm mt-2 ${
            status.type === 'error'
              ? 'border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 text-red-900 dark:text-red-200'
              : status.type === 'success'
              ? 'border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/40 text-green-900 dark:text-green-200'
              : 'border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200'
          }`}
        >
          <p>{status.message}</p>
          {status.type === 'success' && onGoToMessages && (
            <button
              type="button"
              onClick={onGoToMessages}
              className="mt-2 inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-500"
            >
              <i className="fi fi-rr-comment-alt not-italic" />
              View Messages
            </button>
          )}
        </div>
      )}
    </form>
  );
};

export default VehicleSelector;
